import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Container from '../../components/ui/Container';
import RelatedBlogs from '../../components/blog/RelatedBlogs';
import { motion } from 'framer-motion';
import { apiFetch } from '../../utils/api';
import {
  FaCalendarAlt,
  FaClock,
  FaArrowLeft,
  FaLinkedinIn,
  FaTwitter,
  FaWhatsapp,
  FaLink,
  FaCheck,
  FaShareAlt,
  FaChevronRight,
  FaFolder,
  FaPhoneAlt,
} from 'react-icons/fa';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export default function BlogDetailPage() {
  const [blog, setBlog] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const { slug } = useParams();

  // Fetch blog data and active categories
  useEffect(() => {
    const fetchBlogAndCategories = async () => {
      try {
        setLoading(true);
        const [blogRes, catRes] = await Promise.all([
          apiFetch(`/api/blogs/slug/${slug}`),
          apiFetch('/api/categories'),
        ]);

        if (!blogRes.ok) {
          throw new Error('Failed to fetch blog post');
        }
        const blogData = await blogRes.json();
        setBlog(blogData);

        if (catRes.ok) {
          const catData = await catRes.json();
          if (Array.isArray(catData)) {
            // Only keep categories that actually have blogs (blogCount > 0)
            setCategories(catData.filter((c) => (c.blogCount || 0) > 0));
          }
        }
        setError(null);
      } catch (err) {
        setError(err.message || 'Unable to load blog post');
        setBlog(null);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlogAndCategories();
    }
  }, [slug]);

  // Track scroll reading progress
  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollTop;
      const windowHeight =
        document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (windowHeight > 0) {
        const progress = (totalScroll / windowHeight) * 100;
        setScrollProgress(Math.min(100, Math.max(0, progress)));
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCopyLink = () => {
    const currentUrl = window.location.href;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(currentUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      });
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  if (loading) {
    return (
      <div className="w-full pt-44 lg:pt-48 pb-20 bg-slate-50 min-h-screen">
        <Container>
          <div className="mx-auto max-w-4xl space-y-6 animate-pulse">
            <div className="h-6 w-36 rounded-full bg-slate-200" />
            <div className="h-12 w-3/4 rounded-2xl bg-slate-200" />
            <div className="h-6 w-1/2 rounded bg-slate-200" />
            <div className="h-[420px] w-full rounded-3xl bg-slate-200" />
            <div className="space-y-3 pt-6">
              <div className="h-4 w-full rounded bg-slate-100" />
              <div className="h-4 w-5/6 rounded bg-slate-100" />
              <div className="h-4 w-4/6 rounded bg-slate-100" />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="w-full pt-44 pb-24 text-center bg-slate-50 min-h-screen">
        <Container>
          <div className="mx-auto max-w-lg rounded-3xl border border-slate-200 bg-white p-10 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Article Not Found</h2>
            <p className="text-sm text-slate-500 mb-6">
              {error || "The article you're looking for doesn't exist or may have been unpublished."}
            </p>
            <Link to="/blogs" className="btn-primary py-2.5 px-6 rounded-xl inline-flex items-center gap-2 text-sm font-semibold">
              <FaArrowLeft className="text-xs" />
              Back to all articles
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  const formattedDate = new Date(blog.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const authorName = blog.author?.name || blog.authorName || 'Finegal Team';
  const authorInitial = authorName.charAt(0).toUpperCase();

  const shareUrl = encodeURIComponent(window.location.href);
  const shareTitle = encodeURIComponent(blog.title || 'Check out this insight from Finegal Tech Solution');

  return (
    <div className="relative w-full overflow-x-hidden pt-36 sm:pt-40 lg:pt-44 pb-24 bg-[#f8fafc]">
      {/* Sticky Scroll Progress Bar at very top */}
      <div
        className="fixed top-0 left-0 h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-[#01295c] z-50 transition-all duration-150"
        style={{ width: `${scrollProgress}%` }}
      />

      <Container>
        {/* Breadcrumb Navigation */}
        <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
          <Link to="/" className="hover:text-[#01295c] transition">
            Home
          </Link>
          <FaChevronRight className="text-[9px] text-slate-400" />
          <Link to="/blogs" className="hover:text-[#01295c] transition">
            Articles & Insights
          </Link>
          {blog.category && (
            <>
              <FaChevronRight className="text-[9px] text-slate-400" />
              <Link to="/blogs" className="text-amber-600 font-semibold hover:underline">
                {blog.category}
              </Link>
            </>
          )}
        </nav>

        {/* Article Header */}
        <header className="mb-8 max-w-4xl">
          {/* Category Pill */}
          {blog.category && (
            <span className="inline-block rounded-full bg-amber-100/80 border border-amber-300/60 px-3.5 py-1 text-xs font-bold text-amber-950 mb-3.5 shadow-2xs">
              {blog.category}
            </span>
          )}

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 leading-[1.22] mb-4 text-balance">
            {blog.title}
          </h1>

          {/* Short Description */}
          {blog.shortDescription && (
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-normal mb-6">
              {blog.shortDescription}
            </p>
          )}

          {/* Meta bar: Author, Date, Reading time & Share options */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-y border-slate-200/90 py-4 bg-white/60 rounded-2xl px-4 sm:px-6 shadow-2xs">
            {/* Author info */}
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#01295c] to-[#12427a] text-sm font-bold text-white shadow-xs">
                {authorInitial}
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">{authorName}</p>
                <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                  <span className="flex items-center gap-1.5">
                    <FaCalendarAlt className="text-amber-500 text-[10px]" />
                    {formattedDate}
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1.5">
                    <FaClock className="text-amber-500 text-[10px]" />
                    {blog.readingTime || 4} min read
                  </span>
                </div>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400 mr-1 hidden sm:flex items-center gap-1">
                <FaShareAlt className="text-[10px]" /> Share
              </span>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Share on LinkedIn"
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200"
              >
                <FaLinkedinIn className="text-xs" />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Share on Twitter"
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100 hover:text-black hover:border-slate-300"
              >
                <FaTwitter className="text-xs" />
              </a>
              <a
                href={`https://api.whatsapp.com/send?text=${shareTitle}%20${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Share on WhatsApp"
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-green-50 hover:text-green-600 hover:border-green-200"
              >
                <FaWhatsapp className="text-xs" />
              </a>
              <button
                type="button"
                onClick={handleCopyLink}
                title="Copy Article Link"
                className={`relative flex h-8 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition ${
                  copied
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                {copied ? <FaCheck className="text-emerald-600 text-[11px]" /> : <FaLink className="text-[10px]" />}
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
          </div>
        </header>

        {/* Featured Image Showcase */}
        {blog.featuredImage && (
          <motion.div
            variants={fadeUp}
            initial="initial"
            animate="animate"
            transition={{ delay: 0.15 }}
            className="mb-12 overflow-hidden rounded-3xl border border-slate-200/90 shadow-md bg-white max-h-[480px] w-full"
          >
            <img
              src={blog.featuredImage}
              alt={blog.title}
              className="h-full w-full object-cover max-h-[480px]"
            />
          </motion.div>
        )}

        {/* 2-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 pb-16 items-start">
          {/* Main Reading Column */}
          <main className="lg:col-span-8">
            <article className="rounded-3xl border border-slate-200/90 bg-white p-6 sm:p-10 shadow-xs">
              {/* Executive Summary Box */}
              {blog.shortDescription && (
                <div className="mb-8 rounded-2xl border-l-4 border-amber-500 bg-amber-50/70 p-5">
                  <h3 className="text-xs font-extrabold uppercase tracking-wider text-amber-900 mb-1">
                    Executive Summary
                  </h3>
                  <p className="text-sm font-medium leading-relaxed text-slate-700">
                    {blog.shortDescription}
                  </p>
                </div>
              )}

              {/* Formatted Rich Text Content */}
              <div
                className="blog-content prose max-w-none text-slate-800 text-[15.5px] leading-relaxed prose-p:my-2 prose-headings:mt-4 prose-headings:mb-1.5 prose-headings:font-bold prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />

              {/* Tags Cloud */}
              {Array.isArray(blog.tags) && blog.tags.length > 0 && (
                <div className="mt-10 border-t border-slate-100 pt-6">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                    Tagged Topics
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {blog.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-lg bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-200"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Author Bio Box */}
              <div className="mt-8 rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-6 flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#01295c] text-base font-bold text-white shadow-xs">
                  {authorInitial}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{authorName}</h4>
                  <p className="text-xs text-amber-600 font-semibold mb-2">Corporate Advisory Specialist</p>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Committed to simplifying complex financial legislation, compliance architectures, and scalable technology transformations for modern businesses.
                  </p>
                </div>
              </div>

              {/* Navigation: Back to articles */}
              <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between">
                <Link
                  to="/blogs"
                  className="inline-flex items-center gap-2 text-xs font-bold text-[#01295c] hover:text-amber-600 transition"
                >
                  <FaArrowLeft className="text-[10px]" />
                  <span>Back to all insights</span>
                </Link>
                <Link
                  to="/contact"
                  className="text-xs font-bold text-amber-600 hover:underline"
                >
                  Need corporate consultation?
                </Link>
              </div>
            </article>
          </main>

          {/* Sticky Right Sidebar */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-28">
            {/* Consultation CTA Card */}
            <div
              className="rounded-3xl p-6 sm:p-7 text-white shadow-xl relative overflow-hidden"
              style={{ background: 'var(--gradient-hero)' }}
            >
              <div className="relative z-10">
                <span className="inline-block rounded-full bg-amber-400/20 border border-amber-400/40 px-3 py-1 text-[10px] font-bold text-amber-300 uppercase tracking-wider mb-3">
                  Expert Advisory
                </span>
                <h3 className="text-lg sm:text-xl font-black text-white mb-2 leading-snug">
                  Need Expert Assistance for Your Business?
                </h3>
                <p className="text-xs text-white/80 leading-relaxed mb-5">
                  Connect with our Chartered Accountants, Legal Advisors & Tech Consultants for customized guidance.
                </p>
                <Link
                  to="/contact"
                  className="btn-primary w-full text-center text-xs py-3 px-5 rounded-xl font-bold shadow-md block"
                >
                  Schedule a Consultation
                </Link>
              </div>
            </div>

            {/* Categories Widget (Only shown if categories with articles exist) */}
            {categories.length > 0 && (
              <div className="rounded-3xl border border-slate-200/90 bg-white p-6 shadow-xs">
                <div className="flex items-center gap-2 pb-3 mb-3 border-b border-slate-100">
                  <FaFolder className="text-amber-500 text-sm" />
                  <h3 className="text-sm font-bold text-slate-900">
                    Blog Categories
                  </h3>
                </div>
                <div className="space-y-1.5">
                  {categories.map((cat) => {
                    const catName = typeof cat === 'string' ? cat : cat.name;
                    const count = cat.blogCount;
                    return (
                      <Link
                        key={catName}
                        to={`/blogs?category=${encodeURIComponent(catName)}`}
                        className="flex items-center justify-between rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 hover:text-amber-600 group"
                      >
                        <span className="truncate">{catName}</span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {count !== undefined && count !== '' && (
                            <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500 font-bold group-hover:bg-amber-100 group-hover:text-amber-800">
                              {count}
                            </span>
                          )}
                          <FaChevronRight className="text-[9px] text-slate-400 group-hover:text-amber-500 transition-transform group-hover:translate-x-0.5" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Direct Contact Card */}
            <div className="rounded-2xl border border-amber-200/60 bg-amber-50/50 p-4 flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400 text-slate-950 font-bold shadow-2xs">
                <FaPhoneAlt className="text-xs" />
              </div>
              <div className="text-xs">
                <p className="font-bold text-slate-900">Have Questions?</p>
                <a href="tel:+919718431522" className="text-amber-700 font-semibold hover:underline">
                  +91-97184-31522
                </a>
              </div>
            </div>
          </aside>
        </div>

        {/* Related Articles Section */}
        <section className="border-t border-slate-200/80 pt-16 pb-20">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <span className="text-xs font-extrabold uppercase tracking-wider text-amber-600 mb-1 block">
                Keep Reading
              </span>
              <h2 className="text-2xl font-black text-slate-900">Related Articles</h2>
            </div>
            <Link
              to="/blogs"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#01295c] hover:text-amber-600 transition"
            >
              <span>Explore all articles</span>
              <FaChevronRight className="text-[10px]" />
            </Link>
          </div>
          <RelatedBlogs currentSlug={slug} />
        </section>
      </Container>
    </div>
  );
}
